import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["width", "height"]
const _hoisted_2 = ["cx", "cy", "r", "stroke"]
const _hoisted_3 = ["cx", "cy", "r", "stroke", "stroke-dasharray", "stroke-dashoffset"]
const _hoisted_4 = { class: "flex flex-col text-left min-w-0" }

import { computed } from 'vue'
import { getDoctorInitials, capitalizeDoctorName, capitalizeFullName } from '@/utils/string'
import { resolveAvatarSrc } from '@/utils/avatar'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'

type Size = 'sm' | 'md' | 'lg' | 'xl'

const STROKE_WIDTH = 4


export default /*#__PURE__*/_defineComponent({
  __name: 'DoctorInfo',
  props: {
    name: {},
    subtitle: { default: null },
    avatarUrl: { default: null },
    size: { default: 'md' },
    placeholder: { default: '—' },
    npsScore: { default: null },
    truncate: { type: Boolean, default: false },
    fullName: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const AVATAR_PX: Record<Size, number> = {
  sm: 40,
  md: 44,
  lg: 48,
  xl: 56
}



const resolvedAvatarUrl = computed(() => resolveAvatarSrc(props.avatarUrl))

const avatarSize = computed(() => AVATAR_PX[props.size] ?? AVATAR_PX.md)
const radius = computed(() => avatarSize.value / 2 + STROKE_WIDTH / 2)
const svgSize = computed(() => radius.value * 2 + STROKE_WIDTH)
const circumference = computed(() => 2 * Math.PI * radius.value)

const clampedScore = computed(() => {
  if (props.npsScore == null) return 0
  // NPS range is -100..100; normalize to 0..100 for ring fill
  return Math.min(100, Math.max(0, (props.npsScore + 100) / 2))
})

const offset = computed(() => circumference.value * (1 - clampedScore.value / 100))

const arcColor = computed(() => {
  if (props.npsScore == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(props.npsScore).color
})

const initialsTextClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'text-[12px]'
    case 'md':
      return 'text-[13px]'
    case 'lg':
    case 'xl':
      return 'text-[14px]'
    default:
      return 'text-[13px]'
  }
})

const gapClass = computed(() => {
  return props.size === 'lg' || props.size === 'xl' ? 'gap-4' : 'gap-3'
})

const nameClass = computed(() => {
  const hasName = !!props.name
  const color = hasName ? 'text-gray-900' : 'text-gray-400'

  switch (props.size) {
    case 'sm':
      return `font-normal text-[15px] ${color}`
    case 'md':
      return `font-medium text-base ${color}`
    case 'lg':
    case 'xl':
      return `font-medium text-lg ${color}`
    default:
      return `font-medium text-base ${color}`
  }
})

const subtitleClass = computed(() => {
  const hasName = !!props.name
  const color = hasName ? 'text-gray-900' : 'text-gray-400'

  switch (props.size) {
    case 'sm':
      return `text-[13px] ${color}`
    case 'md':
      return `text-xs ${color}`
    case 'lg':
    case 'xl':
      return `text-sm ${color}`
    default:
      return `text-xs ${color}`
  }
})

const displayInitials = computed(() => {
  return props.name ? getDoctorInitials(props.name) : props.placeholder
})

const displayName = computed(() => {
  if (!props.name) return props.placeholder
  return props.fullName ? capitalizeFullName(props.name) : capitalizeDoctorName(props.name)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["flex items-center min-w-0", gapClass.value])
  }, [
    _createElementVNode("div", {
      class: "relative shrink-0",
      style: _normalizeStyle({ width: svgSize.value + 'px', height: svgSize.value + 'px' })
    }, [
      (_openBlock(), _createElementBlock("svg", {
        width: svgSize.value,
        height: svgSize.value,
        class: "absolute top-0 left-0 -rotate-90",
        "aria-hidden": "true"
      }, [
        _createElementVNode("circle", {
          cx: svgSize.value / 2,
          cy: svgSize.value / 2,
          r: radius.value,
          stroke: _unref(NEUTRAL_RING_COLOR),
          "stroke-width": STROKE_WIDTH,
          fill: "none"
        }, null, 8, _hoisted_2),
        (clampedScore.value > 0)
          ? (_openBlock(), _createElementBlock("circle", {
              key: 0,
              cx: svgSize.value / 2,
              cy: svgSize.value / 2,
              r: radius.value,
              stroke: arcColor.value,
              "stroke-width": STROKE_WIDTH,
              fill: "none",
              "stroke-dasharray": circumference.value,
              "stroke-dashoffset": offset.value,
              "stroke-linecap": "round"
            }, null, 8, _hoisted_3))
          : _createCommentVNode("", true)
      ], 8, _hoisted_1)),
      _createVNode(ProtectedAvatar, {
        src: resolvedAvatarUrl.value,
        size: avatarSize.value,
        class: "absolute",
        style: _normalizeStyle({ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' })
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass(["w-full h-full rounded-full bg-primary flex items-center justify-center text-white font-bold", initialsTextClass.value])
          }, _toDisplayString(displayInitials.value), 3)
        ]),
        _: 1
      }, 8, ["src", "size", "style"])
    ], 4),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("span", {
        class: _normalizeClass(["leading-tight", [nameClass.value, { truncate: props.truncate }]])
      }, _toDisplayString(displayName.value), 3),
      (_ctx.subtitle)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["font-bold mt-0.5", [subtitleClass.value, { truncate: props.truncate }]])
          }, _toDisplayString(_ctx.subtitle), 3))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})